import { Button, Select, Skeleton } from '@mantine/core';
import { IconRoute } from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { PageHeader } from '../../PageHeader.js';
import type { StatusTone } from '../../StatusBadge.js';
import { followLink, useShell } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import classes from './NoonReportScreen.module.css';
import type { VoyageSource } from './source.js';
import type { Voyage, VoyagePerformanceView, VoyageReport } from './types.js';
import { type HeadFact, VoyageHeadCard } from './VoyageHeadCard.js';

export const VOYAGE_TONE: Record<Voyage['status'], StatusTone> = {
  planned: 'info',
  active: 'success',
  completed: 'neutral',
  cancelled: 'danger',
};

/** Ekrandaki sefer: seçilen; yoksa sürmekte olan; o da yoksa ilki (iptal edilenler hariç). */
export const pickVoyage = (voyages: readonly Voyage[], id: string | null) =>
  voyages.find((v) => v.id === id) ??
  voyages.find((v) => v.status === 'active') ??
  voyages.find((v) => v.status !== 'cancelled') ??
  null;

export interface FrameData {
  vessels: PersonnelVessel[];
  vessel: PersonnelVessel | null;
  voyages: Voyage[];
  voyage: Voyage;
  reports: VoyageReport[];
  performance: VoyagePerformanceView | null;
  /** Yeniden okur (bağ ya da talep sonrası; ADR 0083); yükleniyor iskeleti göstermez. */
  reload?: () => void;
}

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | {
      kind: 'ready';
      vessels: PersonnelVessel[];
      voyages: Voyage[];
      reports: VoyageReport[];
      performance: VoyagePerformanceView | null;
    };

export interface VoyageFrameProps {
  source: VoyageSource;
  title: string;
  description: string;
  /** Breadcrumb'ın son öğesi (menü adından farklı başlık; G4-04). */
  crumb?: string;
  /** Başlık kartının olguları (kalkış–varış kutusu ayrı). */
  facts: (voyage: Voyage, performance?: VoyagePerformanceView | null) => HeadFact[];
  /** Başlık kartının sağında seçicilerin altında (Edit Voyage). */
  action?: (data: FrameData) => ReactNode;
  /** Yükleniyorken gövdenin iskeleti (düzenin şeklinde; G4-07). */
  skeleton: ReactNode;
  children: (data: FrameData) => ReactNode;
}

/**
 * Overview, Voyage Performance ve Charter Party'nin ortak iskeleti (M6f G4-03, G4-04, G4-07): gemi
 * ve sefer seçimi başlık kartının sağında (referansta "Edit Voyage"ın yeri), raporlar ve sunucunun
 * hesapladığı performans. Hatada başlık yerinde kalır; boşta açıklama ve bağlantı.
 */
export function VoyageFrame({
  source,
  title,
  description,
  crumb,
  facts,
  action,
  skeleton,
  children,
}: VoyageFrameProps) {
  const { t } = useTranslation();
  const shell = useShell();
  // Bağlantıdaki gemi ve sefer (`?vessel=&voyage=`; All Reports "View Details", ADR 0082 Ek)
  const [vesselId, setVesselId] = useState<string | null>(() =>
    new URLSearchParams(shell?.search ?? '').get('vessel'),
  );
  const [voyageId, setVoyageId] = useState<string | null>(() =>
    new URLSearchParams(shell?.search ?? '').get('voyage'),
  );
  const [state, setState] = useState<Load>({ kind: 'loading' });

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setState({ kind: 'loading' });
      try {
        const vessels = await source.vessels();
        const vessel = vessels.find((v) => v.id === vesselId) ?? vessels[0];
        const voyages = vessel ? await source.voyages(vessel.id) : [];
        const voyage = pickVoyage(voyages, voyageId);
        const [reports, performance] = voyage
          ? await Promise.all([source.reports(voyage.id), source.performance(voyage.id)])
          : [[], null];
        // Özet, performans ve CP iptal edilmemiş raporlardan; iptaller Leg / Event izinde (ADR 0077)
        setState({
          kind: 'ready',
          vessels,
          voyages,
          reports: reports.filter((r) => r.status !== 'void'),
          performance,
        });
      } catch (error) {
        setState({ kind: 'error', code: errorCode(error) });
      }
    },
    [source, vesselId, voyageId],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state : null;
  const vessel = ready?.vessels.find((v) => v.id === vesselId) ?? ready?.vessels[0] ?? null;
  const voyages = (ready?.voyages ?? []).filter((v) => v.status !== 'cancelled');
  const voyage = pickVoyage(voyages, voyageId);
  const k = (key: string) => t(`voyage.overview.${key}`);

  const selectors = ready ? (
    <>
      {source.scope === 'web' && ready.vessels.length > 1 ? (
        <Select
          aria-label={t('shell.selectVessel')}
          data={ready.vessels.map((v) => ({ value: v.id, label: v.name }))}
          value={vessel?.id ?? null}
          allowDeselect={false}
          onChange={(value) => {
            if (!value) return;
            setVesselId(value);
            setVoyageId(null);
          }}
        />
      ) : null}
      {voyages.length > 0 ? (
        <Select
          aria-label={k('selectVoyage')}
          data={voyages.map((v) => ({
            value: v.id,
            label: `${v.voyageNo} · ${t(`voyage.voyageStatus.${v.status}`)}`,
          }))}
          value={voyage?.id ?? null}
          allowDeselect={false}
          onChange={(value) => value && setVoyageId(value)}
        />
      ) : null}
    </>
  ) : null;

  const header = (
    <PageHeader title={title} description={description} {...(crumb ? { crumb } : {})} />
  );

  if (state.kind === 'error') {
    return (
      <div className={classes.frame}>
        {header}
        <section className={classes.card}>
          <EmptyState
            tone="danger"
            title={k('error')}
            description={t(`voyage.errors.${state.code}`, {
              defaultValue: t('voyage.errors.GENERIC'),
            })}
            action={
              <Button variant="light" onClick={() => void load()}>
                {t('common.retry')}
              </Button>
            }
          />
        </section>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className={classes.frame} aria-busy="true">
        {header}
        <Skeleton height={120} radius="md" />
        {skeleton}
      </div>
    );
  }

  const data: FrameData | null = voyage
    ? {
        vessels: ready.vessels,
        vessel,
        voyages,
        voyage,
        reports: ready.reports,
        performance: ready.performance,
        reload: () => void load(true),
      }
    : null;

  return (
    <div className={classes.frame}>
      {header}
      <VoyageHeadCard
        label={voyage?.voyageNo ?? vessel?.name ?? title}
        vessel={vessel}
        status={
          voyage
            ? { tone: VOYAGE_TONE[voyage.status], text: t(`voyage.voyageStatus.${voyage.status}`) }
            : null
        }
        route={
          voyage
            ? [
                { label: k('from'), value: voyage.fromPort },
                { label: k('to'), value: voyage.toPort },
              ]
            : null
        }
        facts={voyage ? facts(voyage, data?.performance ?? null) : []}
        aside={
          <>
            {selectors}
            {data && action ? action(data) : null}
          </>
        }
      />
      {data ? (
        children(data)
      ) : (
        <section className={classes.card}>
          <EmptyState
            icon={<IconRoute size={22} stroke={1.8} />}
            title={k('noVoyages')}
            description={source.planner ? k('officePlan') : k('shipPlanned')}
            action={
              source.planner && shell ? (
                <Button
                  component="a"
                  href="/voyage/new"
                  variant="light"
                  onClick={(event: React.MouseEvent<HTMLAnchorElement>) =>
                    followLink(event, '/voyage/new', shell.navigate)
                  }
                >
                  {t('menu.voyageNew')}
                </Button>
              ) : null
            }
          />
        </section>
      )}
    </div>
  );
}
